import React, { useState, useEffect, useMemo, useRef } from "react";
import "@chatscope/chat-ui-kit-styles/dist/default/styles.min.css";
import { MainContainer, ChatContainer, MessageList, Message, MessageInput, TypingIndicator, MessageModel } from "@chatscope/chat-ui-kit-react";
import ChatBotRepository from '@/domain/ChatBot/ChatBotReposiory';
import { ChatBotResponseType } from '@/domain/ChatBot/ChatBotTypes';
import type { DashboardContextValueInterface } from '@/contexts/DashboardContext';
import ChatMessageRenderer from './ChatMessageRenderer';
import { ExpandArrowSvg, ContractArrowSvg } from '@/assets/svg';
import { Icon } from "@hydrodigital/design-system";
import { m } from '@/paraglide/messages';
import { useLocale } from '@/contexts/LocaleContext';

import HydrodigitalLogo from '@/assets/img/hydrodigital-logo.svg?url';
import HydrodigitalSimpleLogo from '@/assets/img/hydrodigital-simple-logo.svg?url';

const RESPONSE_TIMEOUT_MS = 30000;

const COMPACT_SIZE = { width: 340, height: 420 };
const EXPANDED_SIZE = { width: 620, height: 600 };

interface HydroBotMessage extends MessageModel {
  responseType?: ChatBotResponseType;
  content?: string;
  columns?: string[];
  rows?: string[][];
  url?: string;
}

// Custom styles for chat messages
const customStyles = `
  .chatscope-message-sent-bubble {
    background-color: #1976D2 !important;
    color: white !important;
    border-radius: 18px 18px 4px 18px !important;
  }
  
  .chatscope-message-received-bubble {
    background-color: #E3F2FD !important;
    color: #333 !important;
    border-radius: 18px 18px 18px 4px !important;
  }
  
  .chatscope-message-input-container {
    border-radius: 25px !important;
    border: 1px solid #E0E0E0 !important;
    background-color: #F5F5F5 !important;
    padding: 8px 16px !important;
  }
  
  .chatscope-message-input-send-button {
    background-color: #1976D2 !important;
    border-radius: 50% !important;
    width: 36px !important;
    height: 36px !important;
  }
  
  .chatscope-typing-indicator {
    background-color: #E3F2FD !important;
    border-radius: 18px 18px 18px 4px !important;
  }
`;

export type ChatToggleHandler = (forceOpen?: boolean) => void;

interface ChatButtonProps {
  selectedLocation: string;
  selectedDate: DashboardContextValueInterface['selectedDate'];
  toggle: (handler: ChatToggleHandler) => void;
  open?: boolean;
}

const ChatButton = ({ selectedLocation, selectedDate, toggle, open }: ChatButtonProps) => {
  useLocale();
  const chatbotRepository = useMemo(() => new ChatBotRepository(), []);
  const [visible, setVisible] = useState(open !== undefined ? open : false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [showWelcome, setShowWelcome] = useState(true);
  const [messages, setMessages] = useState<HydroBotMessage[]>([]);
  const [zoomedSvgSrc, setZoomedSvgSrc] = useState<string | null>(null);
  const [zoomLevel, setZoomLevel] = useState(1);

  useEffect(() => {
    if (open !== undefined) {
      setVisible(open);
    }
  }, [open]);

  const buildSvgDataUrl = (rawContent: string): string => {
    let svg = rawContent.replace(/\\"/g, '"').replace(/\\'/g, "'");
    if (!/\bwidth=/.test(svg)) {
      const vb = svg.match(/viewBox=["']\s*[\d.]+\s+[\d.]+\s+([\d.]+)\s+([\d.]+)\s*["']/);
      svg = svg.replace('<svg', `<svg width="${vb?.[1] ?? '800'}" height="${vb?.[2] ?? '400'}"`);
    }
    return `data:image/svg+xml,${encodeURIComponent(svg)}`;
  };

  const addResponseMessage = (message: any) => {
    console.log('📨 [ChatButton] addResponseMessage called:', {
      type: message.type,
      contentLength: message.content?.length,
      contentPreview: message.content?.substring(0, 100),
      columns: message.columns,
      rows: message.rows?.length,
    });

    const isSvg = message.type === 'svg' && message.content?.trim().startsWith('<svg');

    const responseMessage: HydroBotMessage = {
      message: isSvg
        ? buildSvgDataUrl(message.content)
        : message.type === 'text' ? message.content : ' ',
      ...(isSvg && { type: 'image' as any }),
      sentTime: new Date().toLocaleTimeString(),
      sender: "Support",
      direction: "incoming",
      position: "single",
      responseType: message.type,
      content: message.type !== 'text' ? message.content : undefined,
      columns: message.columns,
      rows: message.rows,
      url: message.url,
    };
    setMessages((prevMessages) => [...prevMessages, responseMessage]);
  };

  const handleNewUserMessage = async (query: string) => {
    if (typeof query !== 'string' || !query.trim()) {
      addResponseMessage({
        type: 'text',
        content: 'Please enter a valid message.'
      });
      return;
    }

    setIsLoading(true);

    timeoutRef.current = setTimeout(() => {
      setIsLoading(false);
      addResponseMessage({
        type: 'text',
        content: 'The request timed out. Please try again.'
      });
    }, RESPONSE_TIMEOUT_MS);

    try {

      const response = await chatbotRepository.postChatbot(query, selectedLocation);
      if (response.messages && response.messages.length > 0) {
        response.messages.forEach((message) => {
          addResponseMessage(message);
        });
      }
    } catch (error) {
      console.error(error);
      addResponseMessage({
        type: 'text',
        content: 'Sorry, there was an error processing your message.'
      });
    } finally {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      setIsLoading(false);
    }
  };

  const stripHtml = (html: string): string => {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.innerText || tmp.textContent || '';
  };

  const handleSend = async (message: string) => {
    if (isLoading) return;

    const cleanMessage = stripHtml(message).trim();
    if (!cleanMessage) return;

    // Hide welcome screen when first message is sent
    if (showWelcome) {
      setShowWelcome(false);
    }

    const newMessage: HydroBotMessage = {
      message: cleanMessage,
      sentTime: new Date().toLocaleTimeString(),
      sender: "User",
      direction: "outgoing",
      position: "single",
      responseType: 'text',
    };
    setMessages((prevMessages) => [...prevMessages, newMessage]);
    await handleNewUserMessage(cleanMessage);
  };

  useEffect(() => {
    // Inject custom styles
    const styleElement = document.createElement('style');
    styleElement.textContent = customStyles;
    document.head.appendChild(styleElement);

    return () => {
      // Cleanup styles on unmount
      if (document.head.contains(styleElement)) {
        document.head.removeChild(styleElement);
      }
    };
  }, []);

  useEffect(() => {
    toggle((forceOpen) => setVisible(forceOpen !== undefined ? forceOpen : !visible));
  }, [toggle, visible]);

  useEffect(() => {
    if (open !== undefined) setVisible(open);
  }, [open]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const currentSize = isExpanded ? EXPANDED_SIZE : COMPACT_SIZE;

  return (
    <div>
      {zoomedSvgSrc && (
        <div
          onClick={() => setZoomedSvgSrc(null)}
          style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 99999, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
        >
          <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }} onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setZoomLevel(z => Math.max(0.3, +(z - 0.2).toFixed(1)))} style={{ background: 'white', border: 'none', borderRadius: '6px', padding: '6px 14px', cursor: 'pointer', fontSize: '18px', fontWeight: 'bold' }}>−</button>
            <span style={{ color: 'white', lineHeight: '34px', minWidth: '44px', textAlign: 'center', fontSize: '13px' }}>{Math.round(zoomLevel * 100)}%</span>
            <button onClick={() => setZoomLevel(z => Math.min(4, +(z + 0.2).toFixed(1)))} style={{ background: 'white', border: 'none', borderRadius: '6px', padding: '6px 14px', cursor: 'pointer', fontSize: '18px', fontWeight: 'bold' }}>+</button>
            <button onClick={() => setZoomedSvgSrc(null)} style={{ background: 'white', border: 'none', borderRadius: '6px', padding: '6px 14px', cursor: 'pointer', fontSize: '16px', marginLeft: '8px' }}>✕</button>
          </div>
          <div style={{ overflow: 'auto', maxWidth: '95vw', maxHeight: '80vh', backgroundColor: '#fff', borderRadius: '8px', padding: '8px' }} onClick={(e) => e.stopPropagation()}>
            <img
              src={zoomedSvgSrc}
              alt="Chart"
              style={{ display: 'block', width: `${zoomLevel * 800}px`, height: 'auto', borderRadius: '4px' }}
            />
          </div>
        </div>
      )}
      {visible && (
        <div style={{
          position: "fixed",
          bottom: "120px",
          right: "20px",
          width: `${currentSize.width}px`,
          height: `${currentSize.height}px`,
          border: "1px solid #ccc",
          borderRadius: "8px",
          backgroundColor: "#fff",
          display: "flex",
          flexDirection: "column",
          transition: "width 0.25s ease, height 0.25s ease",
          zIndex: 1000,
          boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 10px 10px 20px", borderBottom: "1px solid #ccc", flexShrink: 0, backgroundColor: "#F5F7FB" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <img src={HydrodigitalSimpleLogo} alt="logo" style={{ height: 24 }} />
              {/* <img src="https://via.placeholder.com/30" alt="HydroBot Avatar" style={{ borderRadius: "50%" }} /> */}
              <h3 style={{ margin: 0, fontSize: "16px", fontWeight: "bold" }}>HydroBot Assistant (Beta)</h3>
            </div>
            <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
              <Icon
                name={isExpanded ? "compress-alt" : "arrow-resize-diagonal"}
                onClick={() => setIsExpanded((prev) => !prev)}
                style={{ fontSize: "18px", cursor: "pointer", color: "#666", padding: "4px" }}
              />
              <Icon name="exit" onClick={() => setVisible(false)} style={{ fontSize: "18px", cursor: "pointer", color: "#666", padding: "4px" }} />
            </div>
          </div>
          <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
            {showWelcome && messages.length === 0 ? (
              // Welcome Screen
              <div style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "flex-start",
                padding: "24px 20px 20px",
                textAlign: "center",
                overflowY: "auto"
              }}>

                <h2 style={{
                  margin: 0,
                  fontSize: "20px",
                  fontWeight: "600",
                  color: "#333",
                  marginBottom: "8px"
                }}>
                  HydroBot Assistant (Beta)
                </h2>
                <p style={{
                  margin: 0,
                  fontSize: "13px",
                  color: "#666",
                  marginBottom: "16px",
                  lineHeight: "1.5"
                }}>
                  I'm currently in Beta and can help answer a limited set of wastewater and rainfall questions. For this version, you can ask me:
                </p>
                <ul style={{
                  margin: 0,
                  padding: 0,
                  listStyle: "none",
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  gap: "6px",
                  textAlign: "left"
                }}>
                  {[
                    "Show me when was the last time it rained.",
                    m.chat_example_total_overflow(),
                    m.chat_example_spills_last_month(),
                    "When was the last time the level in a manhole exceeded its high limit?",
                    "Create a table showing the times that a pipe exceeded its high limit.",
                    "Create a histogram with the rainfall volumes for this year.",
                  ].map((suggestion) => (
                    <li
                      key={suggestion}
                      onClick={() => handleSend(suggestion)}
                      style={{
                        padding: "8px 12px",
                        backgroundColor: "#EEF4FF",
                        border: "1px solid #C7D9FF",
                        borderRadius: "8px",
                        fontSize: "12px",
                        color: "#1976D2",
                        cursor: "pointer",
                        lineHeight: "1.4",
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#D6E8FF")}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#EEF4FF")}
                    >
                      {suggestion}
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              // Chat Messages
              <MainContainer style={{ height: "100%", border: "none" }}>
                <ChatContainer>
                  <MessageList
                    typingIndicator={isLoading ? <TypingIndicator content="HydroBot is typing..." /> : null}
                  >
                    {messages.map((msg, i) => {
                      if (msg.responseType === 'svg') {
                        const svgSrc = msg.message?.startsWith('data:image/svg')
                          ? msg.message
                          : buildSvgDataUrl(msg.content ?? '');
                        if (!svgSrc || !svgSrc.includes(',')) return null;
                        return (
                          <div key={i} style={{ display: 'flex', justifyContent: 'flex-start', padding: '4px 10px 4px 16px' }}>
                            <div style={{
                              backgroundColor: '#E3F2FD',
                              borderRadius: '0 18px 18px 18px',
                              padding: '8px',
                              maxWidth: '95%',
                            }}>
                              <div style={{ position: 'relative' }}>
                                <img
                                  src={svgSrc}
                                  alt="Chart"
                                  onClick={() => { setZoomedSvgSrc(svgSrc); setZoomLevel(1); }}
                                  style={{ width: '100%', height: 'auto', display: 'block', borderRadius: '4px', cursor: 'zoom-in' }}
                                />
                                <button
                                  onClick={() => { setZoomedSvgSrc(svgSrc); setZoomLevel(1); }}
                                  title="Expand"
                                  style={{ position: 'absolute', bottom: 6, right: 6, background: 'rgba(255,255,255,0.85)', border: 'none', borderRadius: '4px', padding: '2px 6px', cursor: 'pointer', fontSize: '14px', lineHeight: 1 }}
                                >⤢</button>
                              </div>
                            </div>
                          </div>
                        );
                      }
                      if (msg.responseType && msg.responseType !== 'text') {
                        return (
                          <Message key={i} model={msg}>
                            <Message.CustomContent>
                              <ChatMessageRenderer
                                responseType={msg.responseType}
                                content={msg.content ?? ''}
                                columns={msg.columns}
                                rows={msg.rows}
                                url={msg.url}
                              />
                            </Message.CustomContent>
                          </Message>
                        );
                      }
                      return <Message key={i} model={msg} />;
                    })}
                  </MessageList>
                </ChatContainer>
              </MainContainer>
            )}

            <div style={{ borderTop: "1px solid #ccc", padding: "10px", backgroundColor: "#fff" }}>
              <MessageInput
                placeholder={isLoading ? "HydroBot is responding..." : "Type message here"}
                onSend={handleSend}
                attachButton={false}
                disabled={isLoading}
                sendButton={!isLoading}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatButton;
